import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { GUIDELINE_RULE_DTO_ENUMS } from 'api/repositories/guidelineRules/guidelineRules.constants';
import { CampaignFormStepTwoContext } from '../../campaignFormStepTwo.context';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';

interface FashionCampaignFormStepTwoContextProps {
  needAdditionalPhotos: boolean;
  setNeedAdditionalPhotos: Dispatch<SetStateAction<boolean>>;
  isFormValid: boolean;
  onClickNextButton: () => void;
  initPhotoFields: () => void;
}

const initFashionCampaignFormStepTwoContextData: FashionCampaignFormStepTwoContextProps = {
  needAdditionalPhotos: false,
  setNeedAdditionalPhotos: () => {},
  isFormValid: false,
  onClickNextButton: () => {},
  initPhotoFields: () => {},
};

export const FashionCampaignFormStepTwoContext = createContext<FashionCampaignFormStepTwoContextProps>(
  initFashionCampaignFormStepTwoContextData,
);

export const FashionCampaignFormStepTwoContextProvider = ({ children }: { children: ReactNode }) => {
  const {
    campaignFormMethods,
    guidelineRuleFormMethods,
    showLoader,
    setShowLoader,
    setStep,
    updateCampaignId,
    storeOrUpdateTemporaryCampaignMutate,
    getParsedGuidelineRules,
  } = useContext(CampaignFormContext);
  const { isFormValid: isCommonStepTwoFormValid, onSuccessStoreOrUpdateTemporaryCampaignCallback } =
    useContext(CampaignFormStepTwoContext);

  const [needAdditionalPhotos, setNeedAdditionalPhotos] = useState<boolean>(false);

  const [allowOtherAtTag, hashTagLocation, allowAdvertisementTag] = useWatch({
    control: guidelineRuleFormMethods?.control,
    name: [
      GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.ALLOW_OTHER_AT_TAG,
      GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.HASH_TAG_LOCATION,
      GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.ALLOW_ADVERTISEMENT_TAG,
    ],
  });

  const isFormValid = useMemo(() => {
    if (
      !isCommonStepTwoFormValid ||
      // 타 브랜드 태그 여부를 설정하지 않은 경우
      !allowOtherAtTag?.value ||
      // 해시태그 위치를 설정하지 않은 경우
      !hashTagLocation?.value ||
      // 광고 태그 여부를 설정하지 않은 경우
      !allowAdvertisementTag?.value
    ) {
      return false;
    } else {
      return true;
    }
  }, [isCommonStepTwoFormValid, allowOtherAtTag, hashTagLocation, allowAdvertisementTag]);

  const initPhotoFields = useCallback(() => {
    guidelineRuleFormMethods.setValue(GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PHOTO_MODEL_FULL, []);
    guidelineRuleFormMethods.setValue(GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PHOTO_MODEL_HALF, []);
    guidelineRuleFormMethods.setValue(GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PHOTO_MODEL_CLOSEUP, []);
    guidelineRuleFormMethods.setValue(GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.OTHER_REQUESTS, null);
    setNeedAdditionalPhotos(false);
  }, []);

  const onClickNextButton = () => {
    if (!showLoader) {
      if (updateCampaignId) {
        setStep((prevStep) => prevStep + 1);
      } else {
        setShowLoader(true);
        const outputTypeFl = campaignFormMethods.getValues('outputTypeFl');

        storeOrUpdateTemporaryCampaignMutate({
          campaign: {
            outputTypeFl,
          },
          guidelineRules: getParsedGuidelineRules(),
          options: {
            onSuccess: (response) => {
              onSuccessStoreOrUpdateTemporaryCampaignCallback(response);
            },
          },
        });
      }
    }
  };

  return (
    <FashionCampaignFormStepTwoContext.Provider
      value={{
        needAdditionalPhotos,
        setNeedAdditionalPhotos,
        isFormValid,
        initPhotoFields,
        onClickNextButton,
      }}
    >
      {children}
    </FashionCampaignFormStepTwoContext.Provider>
  );
};
